import Navbar from '@/components/layout/Navbar'
import Footer from '@/components/layout/Footer'
import PageHero from '@/components/layout/PageHero'
import { motion } from 'framer-motion'
import { ArrowRight, FileText, Newspaper, TrendingUp, Users, Briefcase } from 'lucide-react'

const ASX_URL = 'https://www.asx.com.au/asx/share-price-research/company/LYC'
const ASX_ANNOUNCEMENTS = 'https://www.asx.com.au/asx/share-price-research/company/LYC/announcements'

export default function Investors() {
  return (
    <div className="min-h-screen bg-white font-sans text-foreground selection:bg-accent selection:text-white">
      <Navbar light />
      <main className="relative">
        <PageHero
          label="ASX: LYC"
          title="Investor Relations"
          intro="Lynas Rare Earths Limited is listed on the Australian Securities Exchange. Access our latest reports, announcements and shareholder information."
        />
        <div className="container max-w-4xl mx-auto px-6 py-16 md:py-24">
          <div className="space-y-16">
            <motion.section
              id="news"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="scroll-mt-24"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-accent/10 text-accent">
                  <Newspaper className="h-6 w-6" />
                </div>
                <h2 className="text-2xl font-bold font-heading text-primary">News & Updates</h2>
              </div>
              <p className="text-slate-600 leading-relaxed mb-6">
                Stay informed with the latest news, media releases and updates from Lynas Rare Earths. Our team regularly shares updates on operations, sustainability initiatives and market developments.
              </p>
              <a
                href={ASX_ANNOUNCEMENTS}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-accent font-semibold hover:text-accent/80 transition-colors"
              >
                View announcements
                <ArrowRight className="h-4 w-4" />
              </a>
            </motion.section>

            <motion.section
              id="asx"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="scroll-mt-24"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-accent/10 text-accent">
                  <TrendingUp className="h-6 w-6" />
                </div>
                <h2 className="text-2xl font-bold font-heading text-primary">ASX Announcements</h2>
              </div>
              <p className="text-slate-600 leading-relaxed mb-6">
                All Lynas Rare Earths Limited announcements are lodged with the Australian Securities Exchange. Access our current share price, trading information and regulatory announcements.
              </p>
              <a
                href={ASX_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-accent font-semibold hover:text-accent/80 transition-colors"
              >
                ASX company page
                <ArrowRight className="h-4 w-4" />
              </a>
            </motion.section>

            <motion.section
              id="reports"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="scroll-mt-24"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-accent/10 text-accent">
                  <FileText className="h-6 w-6" />
                </div>
                <h2 className="text-2xl font-bold font-heading text-primary">Reports</h2>
              </div>
              <p className="text-slate-600 leading-relaxed mb-6">
                Access our financial reports, annual reports, ESG reports and sustainability reports. Lynas is committed to transparent reporting and governance.
              </p>
              <a
                href={ASX_ANNOUNCEMENTS}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-accent font-semibold hover:text-accent/80 transition-colors"
              >
                View reports
                <ArrowRight className="h-4 w-4" />
              </a>
            </motion.section>

            <motion.section
              id="briefings"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="scroll-mt-24"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-accent/10 text-accent">
                  <Briefcase className="h-6 w-6" />
                </div>
                <h2 className="text-2xl font-bold font-heading text-primary">Investor Briefings</h2>
              </div>
              <p className="text-slate-600 leading-relaxed mb-6">
                Lynas participates in investor briefings and presentations. For investor enquiries, annual reports, presentations and corporate information, please refer to our ASX announcements or contact the Company Secretary.
              </p>
              <a
                href={ASX_ANNOUNCEMENTS}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-accent font-semibold hover:text-accent/80 transition-colors"
              >
                Investor resources
                <ArrowRight className="h-4 w-4" />
              </a>
            </motion.section>

            <motion.section
              id="shareholders"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="scroll-mt-24"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-accent/10 text-accent">
                  <Users className="h-6 w-6" />
                </div>
                <h2 className="text-2xl font-bold font-heading text-primary">Shareholder Information</h2>
              </div>
              <p className="text-slate-600 leading-relaxed mb-6">
                Information for Lynas shareholders including dividend history, governance documents and how to update your contact details. For shareholder enquiries, please contact the Company Secretary.
              </p>
              <a
                href={ASX_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-accent font-semibold hover:text-accent/80 transition-colors"
              >
                Shareholder centre
                <ArrowRight className="h-4 w-4" />
              </a>
            </motion.section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}
